import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import {
  passwordPolicyChecks,
  isPasswordValid,
  passwordsMatch,
} from "@/lib/passwordPolicy";
import { toast } from "sonner";

/**
 * Fase 1L.13G — Redefinição de senha (consumo do token).
 *
 * * Reutiliza a política canônica em ``@/lib/passwordPolicy`` que
 *   espelha ``backend/security/passwords.py::validate_new_password``.
 * * Contrato do endpoint ``POST /auth/reset-password`` inalterado:
 *   envia exatamente ``{token, password}``. A resposta 400 genérica do
 *   backend (token inválido/expirado/usado/substituído) permanece
 *   preservada; o frontend nunca tenta adivinhar a causa.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const checks = passwordPolicyChecks(password);
  const pwdValid = isPasswordValid(password);
  const matches = passwordsMatch(password, confirm);
  const canSubmit = !!token && pwdValid && matches && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!pwdValid) {
      const missing = checks.find((c) => !c.ok);
      setError(missing ? missing.label : "A senha não atende aos critérios.");
      return;
    }
    if (!matches) {
      setError("As senhas não coincidem.");
      return;
    }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Senha redefinida. Faça login.");
      navigate("/login");
    } catch (err) {
      // O backend responde 400 genérico para token inválido/expirado/
      // usado/substituído — não tentamos adivinhar a causa.
      // ``formatApiError`` cai aqui apenas para depuração; a mensagem
      // exibida ao usuário é sempre a orientação segura.
      void formatApiError;
      setError(
        "Não foi possível redefinir a senha. O link pode ser inválido ou ter expirado. Solicite um novo link.",
      );
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Definir nova senha</h1>
        {!token ? (
          <div className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="reset-token-missing">
            Token ausente. <Link to="/esqueci-senha" className="underline" data-testid="reset-request-new-link">Solicitar um novo link</Link>.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-form" noValidate>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="reset-new-password">Nova senha</label>
              <div className="relative">
                <input
                  id="reset-new-password"
                  type={showPwd ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full h-10 rounded-lg border border-slate-200 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={showPwd}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 min-w-11"
                  data-testid="reset-password-toggle"
                >
                  {showPwd ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="reset-confirm-password">Confirmar nova senha</label>
              <div className="relative">
                <input
                  id="reset-confirm-password"
                  type={showConfirm ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full h-10 rounded-lg border border-slate-200 pl-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Ocultar confirmação" : "Mostrar confirmação"}
                  aria-pressed={showConfirm}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700 min-w-11"
                  data-testid="reset-confirm-toggle"
                >
                  {showConfirm ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <ul className="text-xs text-slate-600 space-y-1" data-testid="reset-password-criteria" aria-live="polite">
              {checks.map((c) => (
                <li key={c.key} className="flex items-center gap-2" data-testid={`reset-criteria-${c.key}`} data-ok={c.ok ? "true" : "false"}>
                  {c.ok
                    ? <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
                    : <X className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
                  <span className={c.ok ? "text-emerald-700" : "text-slate-600"}>{c.label}</span>
                </li>
              ))}
              <li className="flex items-center gap-2" data-testid="reset-password-match" data-ok={matches ? "true" : "false"}>
                {matches
                  ? <Check className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
                  : <X className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" />}
                <span className={matches ? "text-emerald-700" : "text-slate-600"}>
                  As duas senhas precisam ser iguais
                </span>
              </li>
            </ul>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert" data-testid="reset-error">
                {error}{" "}
                <Link to="/esqueci-senha" className="underline" data-testid="reset-request-new-link">Solicitar um novo link</Link>.
              </div>
            )}

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="reset-submit-button"
              className="w-full h-10 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {loading ? "Salvando..." : "Redefinir senha"}
            </button>
          </form>
        )}
        <p className="text-sm text-slate-500 mt-6 text-center">
          <Link to="/login" className="hover:underline" data-testid="reset-back-to-login">Voltar ao login</Link>
        </p>
      </div>
    </div>
  );
}
